import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { type SortingState } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbRouterLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { DataTable } from '@/components/data-table'
import {
  advertisementColumns,
  type Advertisement,
} from './AdvertisementColumns'
import { EditIcon, PlusIcon } from '@/components/Icons'
import { PATHROUTES } from '@/constants/path'
import { cn } from '@/lib/utils'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { getAdvertisementList, getSponsorEventDetails } from '@/api/sponsorApi'
import { useQuery } from '@tanstack/react-query'
import {
  API_SPONSORSHIP_STATUS,
  DISPLAY_SPONSORSHIP_STATUS,
  EVENT_STATUS,
  type SponsorShipAdvertisementType,
  type SponsorshipDetailsType,
} from '@/schemas/sponsor.schems'
import CustomLoader from '../CustomLoader'
import {
  isEventCompleted,
  transformAdvertisementData,
  transformSponserEventStatus,
} from '@/constants/sponsor'
import { formatDateDisplay } from '@/utils/dateUtils'
import PaymentConfirmationDialog from './PaymentConfirmationDialog'
import SponsorshipSummaryDialog from './SponsorshipSummaryDialog'
import { paymentStatusClasses } from '@/utils/helper'
import type { SponsorshipStatus } from './SponsorshipColumns'
import { EVENT_TABS } from '@/constants/event'

function SponsorshipStatusBadge({ status }: Readonly<{ status: string }>) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'rounded-full border text-xs font-medium px-2 py-1',
        paymentStatusClasses(status as SponsorshipStatus)
      )}
    >
      {status}
    </Badge>
  )
}

function SponsorshipDetailsData() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [pagination, setPagination] = useState({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  })
  const [isPaymentConfirmationOpen, setIsPaymentConfirmationOpen] =
    useState(true)
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false)

  const { data: sponsorShipDetailsData, isLoading: isLoadingsponsorShip } =
    useQuery({
      queryKey: SPONSOR_QUERY_KEYS.byId(id as string),
      queryFn: () => getSponsorEventDetails(id as string),
      enabled: !!id,
    })
  const sponsorShipDetails: SponsorshipDetailsType =
    sponsorShipDetailsData?.data
  const sponsorShipStatus = transformSponserEventStatus({
    plan: sponsorShipDetails?.plan,
    status: sponsorShipDetails?.status,
  })
  const isActive = sponsorShipDetails?.status === API_SPONSORSHIP_STATUS.ACTIVE

  const eventCompleted = isEventCompleted(
    sponsorShipDetails?.event?.status || EVENT_STATUS.ACTIVE
  )

  const { data: advertisementList, isLoading: isLoadingAdvertisementList } =
    useQuery({
      queryKey: [
        SPONSOR_QUERY_KEYS.advertisementList,
        sorting,
        pageSize,
        currentPage,
        sponsorShipDetails?.subEvent?.id || sponsorShipDetails?.event?.id,
      ],
      queryFn: () => {
        // If subEvent exists, pass subEventId; otherwise pass eventId
        const params = sponsorShipDetails?.subEvent
          ? {
              sort: sorting,
              subEventId: sponsorShipDetails.subEvent.id,
              limit: pageSize,
              page: currentPage,
            }
          : {
              sort: sorting,
              eventId: sponsorShipDetails?.event?.id,
              limit: pageSize,
              page: currentPage,
            }
        return getAdvertisementList(params)
      },
      enabled: !!id && !!sponsorShipDetails && !!sponsorShipDetails?.event?.id,
    })

  const rawAdvertisements: SponsorShipAdvertisementType[] =
    advertisementList?.data?.result || []
  const advertisements: Advertisement[] =
    rawAdvertisements?.map(transformAdvertisementData) || []
  const totalItems = advertisementList?.data?.totalCount || 0
  const totalPages =
    advertisementList?.data?.totalPages || Math.ceil(totalItems / pageSize)

  const handleSelectPackage = () => {
    if (id) {
      navigate(PATHROUTES.DASHBOARD.SELECT_PACKAGE_WITH_ID(id))
    } else {
      navigate(PATHROUTES.DASHBOARD.SELECT_PACKAGE)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1)
  }

  const handleEditPackage = () => {
    navigate(PATHROUTES.DASHBOARD.SELECT_PACKAGE_WITH_ID(id as string))
  }

  const handleProceedToPayment = () => {
    setIsSummaryDialogOpen(true)
  }

  const handleUploadAd = () => {
    navigate(PATHROUTES.DASHBOARD.UPLOAD_AD_WITH_ID(id as string))
  }

  const isNotSelected =
    sponsorShipStatus === DISPLAY_SPONSORSHIP_STATUS.NOT_SELECTED
  const isFailed = sponsorShipStatus === DISPLAY_SPONSORSHIP_STATUS.FAILED

  const showPaymentConfirmation =
    isPaymentConfirmationOpen &&
    source === 'checkout' &&
    sponsorShipStatus === DISPLAY_SPONSORSHIP_STATUS.PAID

  if (isLoadingsponsorShip || !sponsorShipDetails) {
    return <CustomLoader />
  }

  return (
    <main
      className="@container/main flex flex-1 flex-col p-6"
      id="main-content"
      tabIndex={-1}
      aria-label="Sponsorship Details Main Content"
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb className="mb-2.5">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbRouterLink
              to={`${PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT}?tab=${sponsorShipDetails?.subEvent ? EVENT_TABS.SUB_EVENT : EVENT_TABS.MAIN_EVENT}`}
              className="text-ss-neutral-600 hover:text-ss-blue-500 font-medium"
            >
              My Sponsorships
            </BreadcrumbRouterLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-ss-neutral-400" />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-ss-blue-500 font-medium">
              Sponsorship Detail
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Page Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {sponsorShipDetails?.subEvent
                ? sponsorShipDetails.subEvent.name
                : sponsorShipDetails?.event?.name}
            </h1>
            <p className="text-ss-neutral-600 mt-1 text-sm">
              Choose your sponsorship tier and ad placements for your{' '}
              {sponsorShipDetails?.subEvent ? 'sub event' : 'event'}.
            </p>
          </div>
        </div>
      </div>

      {/* Event Completed Banner */}
      {eventCompleted && (
        <div className="bg-ss-neutral-50 border border-ss-neutral-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="h-5 w-5 text-ss-neutral-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-ss-neutral-900">
                Event Completed
              </h3>
              <p className="text-sm text-ss-neutral-600 mt-1">
                This event has been completed. No further actions or
                modifications are allowed for this sponsorship.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sponsorship Detail Card */}
      <div className="bg-card border border-border rounded-lg p-4 flex-1">
        <div className="flex items-center justify-between flex-wrap gap-1 mb-5">
          <h2 className="text-lg font-medium text-foreground mb-0">
            Sponsorship Detail
          </h2>

          {!eventCompleted &&
            sponsorShipStatus ===
              DISPLAY_SPONSORSHIP_STATUS.SPONSORSHIP_REQUESTED && (
              <div className="flex gap-2">
                <Button
                  className="flex items-center gap-2"
                  onClick={handleEditPackage}
                >
                  <EditIcon className="h-4 w-4" />
                  Edit Package
                </Button>
                <Button
                  className="flex items-center gap-2"
                  onClick={handleProceedToPayment}
                >
                  <EditIcon className="h-4 w-4" />
                  Proceed to Payment
                </Button>
              </div>
            )}
          {!eventCompleted && (isNotSelected || isFailed) && (
            <Button
              className="flex items-center gap-2"
              onClick={handleSelectPackage}
            >
              <EditIcon className="h-4 w-4" />
              Select Package
            </Button>
          )}
        </div>

        <div className="border-b border-b-ss-neutral-50 mb-2 lg:mb-4 pb-4">
          {/* Event Information Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[fit-content(300px)_fit-content(300px)_120px_160px_auto] gap-2 lg:gap-8 mb-3">
            {/* First Row */}
            {sponsorShipDetails?.subEvent ? (
              <>
                <div className="space-y-1">
                  <span className="text-sm font-normal text-ss-neutral-600">
                    Sub Event Name
                  </span>
                  <div className="text-foreground font-medium max-w-[300px] break-words wrap-anywhere">
                    {sponsorShipDetails.subEvent.name}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-sm font-normal text-ss-neutral-600">
                    Main Event Name
                  </span>
                  <div className="text-foreground font-medium max-w-[300px] break-words wrap-anywhere">
                    {sponsorShipDetails?.event?.name}
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-1">
                <span className="text-sm font-normal text-ss-neutral-600">
                  Event Name
                </span>
                <div className="text-foreground font-medium max-w-[300px] break-words wrap-anywhere">
                  {sponsorShipDetails?.event?.name}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <span className="text-sm font-normal text-ss-neutral-600">
                Location
              </span>
              <div className="text-foreground font-medium wrap-anywhere min-w-[90px] max-w-[300px]">
                {sponsorShipDetails?.event?.venueAddress || 'Online'}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-sm font-normal text-ss-neutral-600">
                Event Start Date
              </span>
              <div className="text-foreground font-medium wrap-anywhere">
                {formatDateDisplay(sponsorShipDetails?.event?.startDateTime)}
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-sm font-normal text-ss-neutral-600">
                Event End Date
              </span>
              <div className="text-foreground font-medium wrap-anywhere">
                {formatDateDisplay(sponsorShipDetails?.event?.endDateTime)}
              </div>
            </div>

            {/* Second Row */}
            <div className="space-y-1">
              <span className="text-sm font-normal text-ss-neutral-600">
                Sponsorship Status
              </span>
              <div>
                <SponsorshipStatusBadge status={sponsorShipStatus} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[fit-content(300px)_fit-content(600px)] gap-2 lg:gap-12">
            {sponsorShipDetails.amount && (
              <div className="space-y-1">
                <span className="text-sm font-normal text-ss-neutral-600">
                  Amount Paid
                </span>
                <div className="text-foreground font-medium wrap-anywhere">
                  ${sponsorShipDetails.amount}
                </div>
              </div>
            )}
            {sponsorShipDetails.plan && (
              <div className="space-y-1">
                <span className="text-sm font-normal text-ss-neutral-600 wrap-anywhere">
                  Sponsorship Tier
                </span>
                <div className="text-foreground font-medium wrap-anywhere">
                  {sponsorShipDetails.plan.name}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Event's Advertisements Section */}
        <div className="">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <h2 className="text-xl font-semibold text-foreground">
              Event's Advertisements
            </h2>
            <Button
              className="flex items-center gap-2"
              onClick={handleUploadAd}
              disabled={!isActive || eventCompleted}
            >
              <PlusIcon className="h-4 w-4" />
              Upload Ad
            </Button>
          </div>

          {/* Advertisements Table */}
          <DataTable
            parentClassName="lg:mx-0"
            columns={advertisementColumns}
            data={advertisements}
            sorting={sorting}
            setSorting={setSorting}
            pagination={pagination}
            setPagination={setPagination}
            totalItems={totalItems}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyState={{
              image: '/auth/empty-ad.svg',
              title: 'No Ads Yet',
              subtitle: 'Make payment to start reaching festival attendees',
            }}
            isLoading={isLoadingAdvertisementList}
          />
        </div>
        <PaymentConfirmationDialog
          open={showPaymentConfirmation}
          onOpenChange={setIsPaymentConfirmationOpen}
          eventName={
            sponsorShipDetails?.subEvent
              ? sponsorShipDetails?.subEvent?.name
              : sponsorShipDetails?.event?.name || 'Event'
          }
          sponsorShipId={id}
        />
        <SponsorshipSummaryDialog
          open={isSummaryDialogOpen}
          onOpenChange={setIsSummaryDialogOpen}
          sponsorShipId={id}
        />
      </div>
    </main>
  )
}

export default SponsorshipDetailsData
